import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'

import { mergeDeepAll } from '../utils'
import { CompassAPIDataBase, getDataSources } from '../utils/compass'
import type { BaseContext } from './'

export interface Partido {
  absence: number
  abstention: number
  approximate_government_flag: boolean
  approximate_party_flag: boolean
  art_17: number
  government_adherence: number
  party_orientation: string
  government_orientation: string
  no_votes: number
  obstruction: number
  party: string
  party_adherence: number
  yes_votes: number
}

export interface ChartData extends CompassAPIDataBase {
  data: Partido[]
  title: string
}

export const PARTIDO_COLUMNS = [
  'party',
  'yes_votes',
  'no_votes',
  'absence',
  'obstruction',
  'abstention',
  'art_17',
  'party_orientation',
  'party_adherence',
  'government_orientation',
  'government_adherence',
] as const

export const DEPUTIES_VOTES_NAMES: Record<
  typeof PARTIDO_COLUMNS[number],
  string
> = {
  party: 'Partido',
  party_orientation: 'Orien. do part.',
  party_adherence: 'Ader. ao part.',
  government_orientation: 'Orien. do gov.',
  government_adherence: 'Ader. ao gov.',
  yes_votes: 'Sim',
  no_votes: 'Não',
  absence: 'Ausência',
  obstruction: 'Obstrução',
  abstention: 'Abstenção',
  art_17: 'Art. 17',
}

const SERIES_REFERENCE_NAME = [
  'yes_votes',
  'no_votes',
  'absence',
  'obstruction',
  'abstention',
  'art_17',
] as const

type SeriesReference = typeof SERIES_REFERENCE_NAME[number]

const SERIES_COLORS_MAP: Record<SeriesReference, string> = {
  yes_votes: '#44a322',
  no_votes: '#f4643d',
  absence: '#16164d',
  obstruction: '#e7b619',
  abstention: '#9999ff',
  art_17: '#808080',
}

const ABSOLUTE_YAXIS = [
  {
    id: 'absolute',
    opposite: false,
    title: { text: 'Votos' },
  },
]

const PERCENT_YAXIS = [
  {
    id: 'percent',
    opposite: false,
    title: { text: 'Votos' },
    labels: { format: '{value}%' },
    max: 100,
  },
]

export namespace DeputiesVotes {
  export namespace Partido {
    const CONTEXT_ID = 'equity:deputies_votes:partido'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig {
      context: typeof CONTEXT_ID
      ids: string[]
      visualization: 'absolute' | 'percentual'
      title?: string
    }

    const getChartData = async (
      config: ChartConfig,
      modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
        conf
    ) => {
      const response = (await axios(
        modifier({
          url: `/api/tables/deputies-votes/voto-partido`,
          method: 'POST',
          data: { ids: config.ids },
        })
      )) as AxiosResponse<ChartData>

      return response.data
    }

    const getAbsoluteSeries = (data: ChartData) =>
      SERIES_REFERENCE_NAME.map((name) => ({
        color: SERIES_COLORS_MAP[name],
        data: data.data?.map((info) => [info['party'], info[name]]),
        id: name,
        marker: { enabled: false },
        name: DEPUTIES_VOTES_NAMES[name],
        type: 'column',
        yAxis: 'absolute',
        tooltip: { valueDecimals: 0, valueSuffix: '' },
      }))

    const getPartyMetricPercentRatio = (
      value: number,
      allSeries: { party: string; sum: number }[] | undefined,
      currentParty: string
    ) =>
      allSeries
        ? (100 * value) /
          allSeries?.filter(({ party, sum }) =>
            party === currentParty ? sum : 0
          )[0].sum
        : null

    const getPartySumValues = (data: Partido[] | null) =>
      data?.map((info) => ({
        party: info['party'],
        sum: Object.entries(info)
          .map(([name, value]) =>
            SERIES_REFERENCE_NAME.includes(name as SeriesReference) ? value : 0
          )
          .reduce((a, b) => a + b, 0),
      }))

    const getPercentualSeries = (data: ChartData) => {
      const allSeries = getPartySumValues(data.data)
      return SERIES_REFERENCE_NAME.map((name) => ({
        color: SERIES_COLORS_MAP[name],
        data: data.data?.map((info) => [
          info['party'],
          getPartyMetricPercentRatio(info[name], allSeries, info['party']),
        ]),
        id: name,
        marker: { enabled: false },
        name: DEPUTIES_VOTES_NAMES[name],
        type: 'column',
        yAxis: 'percent',
        tooltip: { valueDecimals: 2, valueSuffix: '%' },
      }))
    }

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series =
        config.visualization === 'percentual'
          ? getPercentualSeries(data)
          : getAbsoluteSeries(data)

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis =
        config.visualization === 'percentual' ? PERCENT_YAXIS : ABSOLUTE_YAXIS

      return {
        id: Context.getChartId(config),
        title: '',
        options: mergeDeepAll([
          creditsConfig,
          {
            plotOptions: { column: { stacking: 'normal' } },
            tooltip: { shared: true },
            xAxis: { type: 'category' },
            series,
            yAxis,
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) =>
        `${config.context}:${config.visualization}:${hash(config)}`,
      getChartData,
      getChartProps,
    }
  }
}
